import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import { ActionNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { CardShell, FieldLabel } from "./card-handler";

const CHANNEL_OPTIONS = [
  { label: "Select Channel", value: "" },
  { label: "WhatsApp", value: "WhatsApp" },
  { label: "SMS", value: "SMS" },
  { label: "Email", value: "Email" },
];

function renderPreview(content: string, vars: Record<number, string>): string {
  return content.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const v = vars[Number(n)];
    return v?.trim() ? v : `{{${n}}}`;
  });
}

function parseButtons(tpl: TemplateModule): string[] {
  if (tpl.quick_reply_buttons?.length) {
    return tpl.quick_reply_buttons.map((b) => b.label).filter(Boolean);
  }
  if (!tpl.buttons || !Array.isArray(tpl.buttons)) return [];
  return tpl.buttons
    .map((b: any) => (typeof b === "string" ? b : b?.text ?? b?.payload ?? ""))
    .filter(Boolean);
}

// Meta has approved these exact buttons on feedback_request (verified
// against the relaybridge catalog), but the CRM /templates/ endpoint
// doesn't always return quick_reply_buttons for it — without this
// fallback the card renders no buttons at all even though the customer
// sees them on WhatsApp.
const FALLBACK_BUTTONS_BY_TPL: Record<string, string[]> = {
  feedback_request: ["Good", "Average", "Poor"],
};

// Branch → follow-up template mapping for the Feedback Collection preset
// (design 07-02: "Good, Average and Poor should be functional when u click
// any of them" — clicking a button previews that branch's reply inline,
// under the same card, instead of spawning separate cards). Poor reuses
// the Average reply; it still escalates to Support + assigns an agent via
// the Store Data / Send Internal Notification / Assign Agent cards
// further down the canvas — this preview is customer-reply-only.
const BRANCH_TEMPLATE_BY_LABEL: Record<string, string> = {
  good: "feedback_thanks_good",
  average: "feedback_suggestions",
  poor: "feedback_suggestions",
};

interface SendMessageOutProps {
  node: ActionNode;
  templates?: TemplateModule[];
  onUpdate: (config: Record<string, any>) => void;
  onDelete: () => void;
  onReset: () => void;
  bare?: boolean;
}

export const SendMessageOut: React.FC<SendMessageOutProps> = ({
  node,
  templates = [],
  onUpdate,
  onDelete,
  onReset,
  bare,
}) => {
  const config = node.config ?? {};

  // Filter templates based on selected channel
  const getFilteredTemplates = () => {
    const channel = (config.channel || "WhatsApp").toLowerCase();

    return templates.filter((t) => {
      const tChannel = (t.channel || "").toLowerCase();
      
      if (channel === "whatsapp") {
        return (!tChannel || tChannel === "whatsapp") && t.wa_status === "APPROVED";
      }
      if (channel === "email") {
        return tChannel === "email";
      }
      if (channel === "sms") {
        return tChannel === "sms";
      }
      return true; // fallback
    });
  };

  const filteredTemplates = getFilteredTemplates();

  const selectedTemplate: TemplateModule | null = useMemo(() => {
    const id = config.templateId ?? config.template_id ?? "";
    const name = config.templateName ?? config.template ?? config.template_name ?? "";
    
    return filteredTemplates.find((t) => 
      (id && t.id === id) || (name && t.template_name === name)
    ) ?? null;
  }, [config.templateId, config.template_id, config.templateName, config.template, config.template_name, filteredTemplates]);

  const variables: Record<number, string> = config.variables ?? {};
  const previewBody = selectedTemplate ? renderPreview(selectedTemplate.content ?? "", variables) : "";
  const buttons = selectedTemplate
    ? (parseButtons(selectedTemplate).length
        ? parseButtons(selectedTemplate)
        : FALLBACK_BUTTONS_BY_TPL[selectedTemplate.template_name] ?? [])
    : [];

  const selectedBranch: string = config._selectedBranch ?? "";
  const branchTemplateName = BRANCH_TEMPLATE_BY_LABEL[selectedBranch.toLowerCase()];
  const branchTemplate = branchTemplateName
    ? templates.find((t) => t.template_name === branchTemplateName) ?? null
    : null;

  const handleBranchClick = (label: string) => {
    onUpdate({ ...config, _selectedBranch: label });
  };

  const handleChannelChange = (channel: string) => {
    onUpdate({
      ...config,
      channel,
      templateId: "",
      templateName: "",
      variables: {},
    });
  };

  const handleTemplateChange = (templateId: string) => {
    const tpl = filteredTemplates.find((t) => t.id === templateId);
    if (!tpl) return;

    onUpdate({
      ...config,
      templateId: tpl.id,
      templateName: tpl.template_name,
      language: tpl.language ?? (config.channel === "Email" ? "en" : "en_US"),
      variables: {},
    });
  };

  return (
    <CardShell
      title="Send Message"
      description="Select and send a message template to customers"
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        {/* Channel Selector */}
        <div className="mb-4">
          <FieldLabel>Select Channel</FieldLabel>
          <div className="relative">
            <select
              value={config.channel ?? "WhatsApp"}
              onChange={(e) => handleChannelChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-gray-900"
            >
              {CHANNEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Template Selector */}
        <div className="mb-4">
          <FieldLabel>Select Template</FieldLabel>
          <div className="relative">
            <select
              value={config.templateId ?? ""}
              onChange={(e) => handleTemplateChange(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-gray-900"
            >
              <option value="">— Select Template —</option>
              {filteredTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-3 text-[#7B7B7B] font-semibold dark:text-gray-500 pointer-events-none" />
          </div>
        </div>

        {/* Preview */}
        {selectedTemplate && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
            {selectedTemplate.header_type === "image" && selectedTemplate.header_content && (
              <img
                src={selectedTemplate.header_content}
                alt="Header"
                className="w-full h-40 object-cover"
              />
            )}

            <div className="p-4 text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-line">
              {previewBody}
            </div>

            {buttons.length > 0 && (
              <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100">
                {buttons.map((btn, i) => {
                  const active = selectedBranch.toLowerCase() === btn.toLowerCase();
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleBranchClick(btn)}
                      className={`w-full px-4 py-3 text-center font-medium transition-colors cursor-pointer flex items-center justify-between group ${
                        active ? "bg-indigo-50 text-indigo-700" : "text-indigo-600 hover:bg-indigo-50"
                      }`}
                    >
                      <span>{btn}</span>
                      <div
                        className={`w-5 h-5 rounded-full border-2 border-indigo-500 transition-colors flex items-center justify-center ${
                          active ? "bg-indigo-500" : "bg-white dark:bg-gray-900 group-hover:bg-indigo-500"
                        }`}
                      >
                        <div
                          className={`w-2.5 h-2.5 rounded-full transition-colors ${
                            active ? "bg-white dark:bg-gray-900" : "bg-indigo-500 group-hover:bg-white"
                          }`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Branch reply preview — clicking Good/Average/Poor above shows
                what gets sent back for that reply, under this same card. */}
            {selectedBranch && branchTemplateName && (
              <div className="border-t border-gray-100 dark:border-gray-800 p-3 bg-indigo-50/40">
                <p className="text-[10px] font-medium text-indigo-500 mb-1">
                  When customer replies "{selectedBranch}"
                </p>
                {branchTemplate ? (
                  <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {branchTemplate.content}
                  </p>
                ) : (
                  <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 italic">
                    Template "{branchTemplateName}" not found — create/approve it to preview the reply.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
    </CardShell>
  );
};